/**
 * Telemetry-only unit conversion helpers for the Pass Analytics table and
 * graph. Trimmed lift of data360's `src/utils/unitUtils.ts` — kept narrow on
 * purpose so non-telemetry surfaces (WPS forms, fitment readings, pipe setup)
 * are unaffected.
 *
 * The conversion factor matches data360 (`* 25.4` for inch-based values,
 * `/ 25.4` for kJ/in → kJ/mm) so a user flipping the setting in either app
 * sees the same numbers on the same browser.
 */
import { getUnitSetting } from './userUnitSetting'

const IN_TO_MM = 25.4

const INCH_BASED_KEYS = new Set([
  'distance',
  'widthMil',
  'wireIpm',
  'travelSpeed',
  'totWireIn'
])

const HEAT_KEYS = new Set(['rprtHeat', 'calcHeat', 'trueEnergy'])

const INVARIANT_KEYS = new Set([
  'volts',
  'amps',
  'targetVlt',
  'csBias',
  'bpm',
  'tilt',
  'duration',
  'sampleTime',
  'startTime',
  'evtNo'
])

const SUFFIX_TABLE: Record<string, { imperial: string; metric: string }> = {
  volts: { imperial: 'V', metric: 'V' },
  amps: { imperial: 'A', metric: 'A' },
  targetVlt: { imperial: 'V', metric: 'V' },
  csBias: { imperial: 'A', metric: 'A' },
  bpm: { imperial: 'BPM', metric: 'BPM' },
  travelSpeed: { imperial: 'ipm', metric: 'mm/s' },
  wireIpm: { imperial: 'ipm', metric: 'mm/s' },
  widthMil: { imperial: 'mil', metric: 'mm' },
  distance: { imperial: 'in', metric: 'mm' },
  tilt: { imperial: '°', metric: '°' },
  totWireIn: { imperial: 'in', metric: 'mm' },
  rprtHeat: { imperial: 'kJ/in', metric: 'kJ/mm' },
  calcHeat: { imperial: 'kJ/in', metric: 'kJ/mm' },
  trueEnergy: { imperial: 'kJ/in', metric: 'kJ/mm' },
  duration: { imperial: 's', metric: 's' }
}

export function getUnitFromSettings(): 'in' | 'mm' {
  return getUnitSetting() === 'Metric' ? 'mm' : 'in'
}

export function convertWeldDataValue(
  value: number | null | undefined,
  paramKey: string
): number | null {
  if (value === null || value === undefined) return null
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  if (getUnitFromSettings() === 'in') return value
  if (INVARIANT_KEYS.has(paramKey)) return value
  if (HEAT_KEYS.has(paramKey)) return value / IN_TO_MM
  if (INCH_BASED_KEYS.has(paramKey)) return value * IN_TO_MM
  return value
}

export function getParameterUnitSuffix(paramKey: string): string {
  const entry = SUFFIX_TABLE[paramKey]
  if (!entry) return ''
  return getUnitFromSettings() === 'mm' ? entry.metric : entry.imperial
}
